/**
 * useCADHandlers — all mouse/keyboard handlers and side-effect hooks.
 *
 * Receives the full CAD state object from useCADState and returns
 * stable callback/handler references for CADCanvas and DPL Jade to wire up.
 *
 * Zero JSX — this file is pure logic.
 */
import { useCallback, useEffect } from "react";
import { MATERIALS, WORLD_SNAP }  from "../constants";
import { s2w, w2s, snapWP }       from "../utils/coords";
import { Geo }                     from "../utils/geo";
import { computeSnap }             from "../engine/snap";
import { mkEmpty }                 from "../engine/dcel";
import { migrateState }            from "../engine/migrate";
import { loadProject }             from "./useSave";
import { supabase }                from "../lib/supabase";
import { downloadDXF }             from "../engine/dxfExport";
import { wallSchedule, openingSchedule, roomSchedule, fullBOQ } from "../engine/scheduleExport";
import {
  mutLockEdgeLength, mutUnlockEdge,
  mutMakePerpendicular, mutMakeParallel,
  mutMakeHorizontal, mutMakeVertical,
  mutSetEdgeAngle, removeConstraint, mutMakeEqualLength,
} from "../engine/constraintSolver";
import {
  mutAddCircle, mutAddArc3Pt, mutAddArc,
  mutAddSpline, mutAddSplinePt, mutCloseSpline,
  mutAddPolyline, mutDeleteCircle, mutDeleteArc,
  mutDeleteSpline, mutDeletePolyline,
} from "../engine/primitives";
import { mutSetDrawingMode } from "../engine/layerMutations";
import {
  mutFillet, mutChamfer, mutExtend, mutAddPolygon,
  mutBreakEdge, mutSetLinetype, mutSetLineweight,
  mutSetEntityColor, mutSetPanelProps,
} from "../engine/editMutationsV7";
import {
  mutDefineBlock, mutInsertBlock, mutDeleteBlockDef,
  mutDeleteBlockRef, mutExplodeBlock, mutRenameBlock,
} from "../engine/blockMutations";
import { applyAIMutations, buildAIPrompt, callAIDrawEndpoint } from "../engine/aiDrawing";
import { healGraph } from "../engine/heal";
import {
  exportGCode, exportCutList, downloadGCode, downloadCutList,
  nestPanels, buildPanelList, CNC_MACHINES, SHEET_SIZES,
} from "../engine/cncEngine";
import { exportDXFWithPrimitives } from "../engine/dxfExport";
import {
  mutAddWall, mutMoveNode, mutMoveNodes, mutDeleteEdge, mutDeleteOpening,
  mutResizeWall, mutSetRoom, mutAddOpening, mutHeal,
  mutOffsetEdge, mutOffsetFace, getOffsetPoints,
} from "../engine/mutations";
import {
  mutRotate, mutScale, mutMirror, mutCopyEdges,
  mutTrim, mutArrayRect, mutArrayPolar,
} from "../engine/editMutations";import {
  mutPlaceSymbol, mutMoveSymbol,
  mutAddAnnotation, mutDeleteAnnotation, mutMoveAnnotation,
  mutAddManualDim,  mutDeleteManualDim,
  mutAddGridLine,   mutDeleteGridLine,
  mutUpdateDrawing,
  mutDeleteMany, mutMoveMany,
  mutSaveLayerState, mutRestoreLayerState, mutDeleteLayerState,
  mutAddAngularDim,
} from "../engine/layerMutations";
import { SESSION_PROJECT_ID } from "./useCADState";

// Symbol type → layer id mapping (kept here so both CLI and click-handler share it)
const SYMBOL_LAYER_MAP = {
  POWER_13A:"electrical", POWER_5A:"electrical",
  SWITCH_1:"electrical",  SWITCH_2:"electrical",
  LIGHT_CEILING:"electrical", LIGHT_EXHAUST:"electrical",
  DB_BOARD:"electrical",  AC_UNIT:"electrical",
  WC:"plumbing",          BASIN:"plumbing",
  SHOWER:"plumbing",      BATHTUB:"plumbing",
  SINK_KITCHEN:"plumbing",FLOOR_DRAIN:"plumbing",
  BED_SINGLE:"furniture", BED_DOUBLE:"furniture",
  SOFA_3:"furniture",     DINING_4:"furniture",
  CHAIR:"furniture",      KITCHEN_COUNTER:"furniture",
  WARDROBE:"furniture",   DESK:"furniture",
  COLUMN_SQUARE:"structural", COLUMN_ROUND:"structural",
  STAIR_UP:"structural",
};

export function useCADHandlers(cad) {
  const {
    st, commit, beginDrag, commitDrag, undo, redo,
    user, setHydrated,
    svgRef, panRef, isPanning, lastClick, snapRef,
    pan, setPan, zoom, setZoom, setSvgSize,
    mode, setMode, matId, wallH,
    wStart, setWStart, setSnap, snapLock, setSnapLock,
    clearSel,
    sel, setSel, setSelAnnotation, selAnnotation,
    dragNode, setDragNode, dragPosMm, setDragPosMm,
    dragNodes,  setDragNodes,
    dragAnchor, setDragAnchor,
    dragDelta,  setDragDelta,
    setEditingDim, setRoomPopup, setOpeningPopup,
    setShowHealLog, setShowTemplates, setShowDrawing,
    setShowLayerPanel, setActiveSymbol,
    activeLayer, activeSymbol,
    offsetDist, setOffsetDist,
    offsetSide, setOffsetSide,
    offsetHoverEdge, setOffsetHoverEdge,
    selMany, setSelMany,
    selBox,  setSelBox,
    selBoxAnchor, setSelBoxAnchor,
    dragSymbol, setDragSymbol,
    dragSymPos, setDragSymPos,
    dragAnn,    setDragAnn,
    dragAnnPos, setDragAnnPos,
    isFullscreen, setIsFullscreen,
    showCmdLine, setShowCmdLine,
    showMenuBar, setShowMenuBar,
    setPendingAnnotation, setAnnotationDraft, setPendingGridLine,
    transformAngle, transformScale,
    arrayRows, arrayCols, arrayRowSpacing, arrayColSpacing,
    arrayCount, arrayTotalAngle,
    mirrorAxisStart, setMirrorAxisStart,
    setShowTransformPanel,
    adimPts, setAdimPts,
    trimTarget, setTrimTarget,
    selPrim, setSelPrim,
    primDrawPts, setPrimDrawPts,
    primDrawType, setPrimDrawType,
    grid,
  } = cad;

  // ── Project hydration ─────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const saved = await loadProject(SESSION_PROJECT_ID, user?.id ?? null);
        if (!cancelled && saved && Object.keys(saved.nodes || {}).length > 0) {
          commit(() => migrateState(saved));
        }
      } catch (e) {
        console.error("[DPL Jade] Failed to load project:", e);
      } finally {
        if (!cancelled) setHydrated(true);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount

  // ── SVG resize observer ───────────────────────────────────────────────────────
  useEffect(() => {
    const obs = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      setSvgSize({ w: width, h: height });
    });
    if (svgRef.current) obs.observe(svgRef.current);
    return () => obs.disconnect();
  }, [svgRef, setSvgSize]);

  // ── Coordinate helper — screen → world mm ─────────────────────────────────────
  const evW = useCallback(e => {
    const r = svgRef.current?.getBoundingClientRect();
    if (!r) return { x: 0, y: 0 };
    return s2w({ x: e.clientX - r.left, y: e.clientY - r.top }, pan, zoom);
  }, [pan, zoom, svgRef]);

  // ── Command-line dispatch ─────────────────────────────────────────────────────
  const handleCommand = useCallback(cmd => {
    switch (cmd.type) {
      case "MODE":
        setMode(cmd.mode); setWStart(null); setSnapLock(null);
        setPrimDrawPts([]); setPrimDrawType(null);
        break;
      case "HEAL":    commit(mutHeal); break;
      case "CLEAR":   commit(() => mkEmpty()); clearSel(); break;
      case "UNDO":    undo(); break;
      case "REDO":    redo(); break;
      case "ZOOM":    setZoom(cmd.zoom); break;
      case "LAYER_VISIBLE":
        commit(s => ({
          ...s,
          layers: {
            ...s.layers,
            [cmd.layerId]: { ...s.layers?.[cmd.layerId], visible: cmd.visible },
          },
        }));
        break;
      case "LAYER_LIST":
        if (cmd.callback) cmd.callback(st.layers || {});
        break;
      case "DRAW_WALL":
        commit(s => mutAddWall(cmd.ptA, cmd.ptB, "wall", 230, wallH, matId, s));
        break;
      case "DRAW_RECT":
        commit(s => {
          let ns = mutAddWall({x:cmd.x,       y:cmd.y},       {x:cmd.x+cmd.w, y:cmd.y},       "wall",230,wallH,matId,s);
          ns = mutAddWall({x:cmd.x+cmd.w, y:cmd.y},       {x:cmd.x+cmd.w, y:cmd.y+cmd.h}, "wall",230,wallH,matId,ns);
          ns = mutAddWall({x:cmd.x+cmd.w, y:cmd.y+cmd.h}, {x:cmd.x,       y:cmd.y+cmd.h}, "wall",230,wallH,matId,ns);
          ns = mutAddWall({x:cmd.x,       y:cmd.y+cmd.h}, {x:cmd.x,       y:cmd.y},       "wall",230,wallH,matId,ns);
          return ns;
        });
        break;
      case "GRID_LINE":
        commit(s => mutAddGridLine(cmd.axis, cmd.position, cmd.label, s));
        break;
      case "MOVE_MANY": {
        // Move the entire multi-selection by dx,dy (mm)
        if (cmd.dx !== undefined && cmd.dy !== undefined) {
          commit(s => mutMoveMany(selMany, cmd.dx, cmd.dy, s));
        }
        break;
      }
      case "ROTATE": {
        // Rotate selected edges/nodes by angle around optional pivot
        const angle = cmd.angle ?? transformAngle;
        if (selMany.edges.length || selMany.nodes.length) {
          commit(s => mutRotate(selMany, angle, cmd.pivot || null, s));
        }
        break;
      }
      case "SCALE": {
        const factor = cmd.factor ?? transformScale;
        if (selMany.edges.length || selMany.nodes.length) {
          commit(s => mutScale(selMany, factor, cmd.pivot || null, s));
        }
        break;
      }
      case "MIRROR": {
        // Mirror selected edges. axisA/axisB define the mirror line.
        if (cmd.axisA && cmd.axisB && selMany.edges.length) {
          commit(s => mutMirror(selMany, cmd.axisA, cmd.axisB, cmd.deleteSrc || false, s));
        }
        break;
      }
      case "COPY_EDGES": {
        if (selMany.edges.length) {
          const dx = cmd.dx || 0, dy = cmd.dy || 0;
          commit(s => mutCopyEdges(selMany.edges, dx, dy, s));
        }
        break;
      }
      case "TRIM": {
        if (cmd.edgeId && cmd.cutEdgeId) {
          commit(s => mutTrim(cmd.edgeId, cmd.cutEdgeId, cmd.keepSide || "A", s));
        }
        break;
      }
      case "ARRAY_RECT": {
        if (selMany.edges.length) {
          const cols = cmd.cols ?? arrayCols;
          const rows = cmd.rows ?? arrayRows;
          const cs   = cmd.colSpacing ?? arrayColSpacing;
          const rs   = cmd.rowSpacing ?? arrayRowSpacing;
          commit(s => mutArrayRect(selMany.edges, cols, rows, cs, rs, s));
        }
        break;
      }
      case "ARRAY_POLAR": {
        if (selMany.edges.length) {
          const count = cmd.count ?? arrayCount;
          const total = cmd.totalAngle ?? arrayTotalAngle;
          const center = cmd.center || null;
          commit(s => mutArrayPolar(selMany.edges, count, center, total, s));
        }
        break;
      }
      case "MIRROR_MODE":
        setMode("mirror");
        setMirrorAxisStart(null);
        break;
      case "EXPORT_DXF":
        downloadDXF(st, `${st.project?.name || "dpl-jade"}.dxf`);
        break;
      case "EXPORT_WALL_SCHEDULE":   wallSchedule(st);    break;
      case "EXPORT_OPENING_SCHEDULE":openingSchedule(st); break;
      case "EXPORT_ROOM_SCHEDULE":   roomSchedule(st);    break;
      case "EXPORT_BOQ":             fullBOQ(st);         break;
      // ── Constraint commands ────────────────────────────────────────────────
      case "LOCK_LENGTH": {
        const eId = cmd.eId || sel?.edge;
        if (eId) commit(s => mutLockEdgeLength(eId, cmd.length || null, s));
        break;
      }
      case "UNLOCK_EDGE": {
        const eId = cmd.eId || sel?.edge;
        if (eId) commit(s => mutUnlockEdge(eId, s));
        break;
      }
      case "MAKE_PERPENDICULAR": {
        const edges = cmd.edges || selMany?.edges?.slice(0, 2);
        if (edges?.length === 2) commit(s => mutMakePerpendicular(edges[0], edges[1], s));
        break;
      }
      case "MAKE_PARALLEL": {
        const edges = cmd.edges || selMany?.edges?.slice(0, 2);
        if (edges?.length === 2) commit(s => mutMakeParallel(edges[0], edges[1], s));
        break;
      }
      case "MAKE_HORIZONTAL": {
        const eId = cmd.eId || sel?.edge;
        if (eId) commit(s => mutMakeHorizontal(eId, s));
        break;
      }
      case "MAKE_VERTICAL": {
        const eId = cmd.eId || sel?.edge;
        if (eId) commit(s => mutMakeVertical(eId, s));
        break;
      }
      case "SET_ANGLE": {
        const edges = cmd.edges || selMany?.edges?.slice(0, 2);
        if (edges?.length === 2 && cmd.angle != null)
          commit(s => mutSetEdgeAngle(edges[0], edges[1], cmd.angle, s));
        break;
      }
      case "MAKE_EQUAL_LENGTH": {
        const edges = cmd.edges || selMany?.edges?.slice(0, 2);
        if (edges?.length === 2) commit(s => mutMakeEqualLength(edges[0], edges[1], s));
        break;
      }
      case "REMOVE_CONSTRAINT":
        if (cmd.constraintId) commit(s => removeConstraint(cmd.constraintId, s));
        break;
      // ── Drawing mode ────────────────────────────────────────────────────────
      case "SET_DRAWING_MODE":
        commit(s => mutSetDrawingMode(cmd.mode, s));
        break;
      // ── Primitive draw ───────────────────────────────────────────────────
      case "DRAW_CIRCLE": {
        // CIRCLE cx,cy r  — draw circle from coords
        const { cx, cy, r, layerId } = cmd;
        if (cx != null && cy != null && r != null)
          commit(s => mutAddCircle(cx, cy, r, layerId || "walls", s));
        break;
      }
      case "DRAW_ARC": {
        const { cx, cy, r, startAngle, endAngle, layerId } = cmd;
        if (cx != null)
          commit(s => mutAddArc(cx, cy, r, startAngle, endAngle, layerId || "walls", s));
        break;
      }
      case "DRAW_SPLINE":
        if (cmd.pts?.length >= 2)
          commit(s => mutAddSpline(cmd.pts, cmd.closed || false, cmd.layerId || "walls", s));
        break;
      case "DRAW_POLYLINE":
        if (cmd.pts?.length >= 2)
          commit(s => mutAddPolyline(cmd.pts, cmd.closed || false, cmd.filled || false, cmd.layerId || "walls", s));
        break;
      case "DELETE_PRIMITIVE": {
        const { primType, primId } = cmd;
        if (primType === "circle")   commit(s => mutDeleteCircle(primId, s));
        if (primType === "arc")      commit(s => mutDeleteArc(primId, s));
        if (primType === "spline")   commit(s => mutDeleteSpline(primId, s));
        if (primType === "polyline") commit(s => mutDeletePolyline(primId, s));
        setSelPrim(null);
        break;
      }

      // ── v7: Fillet / chamfer / extend / polygon ─────────────────────────────
      case "FILLET": {
        const edges = cmd.edges || selMany?.edges?.slice(0, 2);
        if (edges?.length >= 2 && cmd.radius > 0)
          commit(s => mutFillet(edges[0], edges[1], cmd.radius, s));
        break;
      }
      case "CHAMFER": {
        const edges = cmd.edges || selMany?.edges?.slice(0, 2);
        if (edges?.length >= 2)
          commit(s => mutChamfer(edges[0], edges[1], cmd.dist1, cmd.dist2 ?? cmd.dist1, s));
        break;
      }
      case "EXTEND_EDGE": {
        if (cmd.edgeId && cmd.boundaryId)
          commit(s => mutExtend(cmd.edgeId, cmd.boundaryId, s));
        break;
      }
      case "POLYGON": {
        const snap = snapRef.current?.point || { x: 0, y: 0 };
        commit(s => mutAddPolygon(
          cmd.cx ?? snap.x, cmd.cy ?? snap.y,
          cmd.r, cmd.sides, cmd.rotation || 0, "walls", s
        ));
        break;
      }
      case "BREAK_EDGE": {
        if (cmd.edgeId && cmd.pt)
          commit(s => mutBreakEdge(cmd.edgeId, cmd.pt, s));
        break;
      }

      // ── v7: Line properties ─────────────────────────────────────────────────
      case "SET_LINETYPE": {
        const { entityType, entityId, linetype } = cmd;
        if (entityId) commit(s => mutSetLinetype(entityType||"edge", entityId, linetype, s));
        break;
      }
      case "SET_LINEWEIGHT": {
        const { entityType, entityId, lineweight } = cmd;
        if (entityId) commit(s => mutSetLineweight(entityType||"edge", entityId, lineweight, s));
        break;
      }
      case "SET_PANEL_PROPS": {
        if (cmd.eId) commit(s => mutSetPanelProps(cmd.eId, cmd.props, s));
        break;
      }

      // ── v7: CNC exports ─────────────────────────────────────────────────────
      case "EXPORT_GCODE": {
        const machineId = cmd.machine || "router";
        const opts = { kerf: cmd.kerf !== false, materialThickness: cmd.thickness || 18 };
        downloadGCode(st, machineId, opts);
        break;
      }
      case "EXPORT_CUTLIST":
        downloadCutList(st);
        break;
      case "EXPORT_DXF_FULL":
        // DXF with circles, arcs, polylines
        {
          const dxf = exportDXFWithPrimitives(st);
          const blob = new Blob([dxf], { type: "application/dxf" });
          const a = document.createElement("a");
          a.href = URL.createObjectURL(blob);
          a.download = `jade-${Date.now()}.dxf`;
          a.click();
          URL.revokeObjectURL(a.href);
        }
        break;
      case "NEST_PANELS": {
        const panels = buildPanelList(st);
        const sheetKey = cmd.sheet || "8x4";
        const SHEETS = { "8x4":{w:2440,h:1220}, "6x3":{w:1830,h:915}, "2400x1200":{w:2400,h:1200} };
        const sheet = SHEETS[sheetKey] || SHEETS["8x4"];
        const result = nestPanels(panels, sheet.w, sheet.h);
        console.log("[Nest]", result);
        break;
      }

      // ── Blocks ───────────────────────────────────────────────────────────────
      case "DEFINE_BLOCK": {
        const edgeIds = cmd.edgeIds || selMany?.edges || [];
        if (edgeIds.length || cmd.primIds) {
          commit(s => mutDefineBlock(cmd.name || "Block", edgeIds, cmd.primIds || {}, s));
        }
        break;
      }
      case "INSERT_BLOCK": {
        const pt = snapRef.current?.point || { x: 0, y: 0 };
        if (cmd.defId)
          commit(s => mutInsertBlock(cmd.defId, cmd.x ?? pt.x, cmd.y ?? pt.y, cmd.rotation || 0, cmd.scale || 1, s));
        break;
      }
      case "EXPLODE_BLOCK": {
        if (cmd.refId) commit(s => healGraph(mutExplodeBlock(cmd.refId, s)));
        break;
      }
      case "DELETE_BLOCK_DEF": {
        if (cmd.defId) commit(s => mutDeleteBlockDef(cmd.defId, s));
        break;
      }

      // ── AI drawing ───────────────────────────────────────────────────────────
      case "AI_DRAW": {
        if (!cmd.prompt) break;
        (async () => {
          try {
            const { data: { session } } = await supabase.auth.getSession();
            const fullPrompt = buildAIPrompt(cmd.prompt, st);
            const mutations  = await callAIDrawEndpoint(fullPrompt, session);
            commit(s => healGraph(applyAIMutations(mutations, s)));
          } catch (e) { console.error("[AI_DRAW]", e); }
        })();
        break;
      }

      // ── Floor switching ──────────────────────────────────────────────────────
      case "SET_FLOOR": {
        const idx = Number(cmd.floor ?? 0);
        commit(s => ({ ...s, floorIndex: idx }));
        break;
      }

      case "SET_HATCH": {
        // Apply hatch to selected primitive or selected face
        const { patternId } = cmd;
        if (sel.primitive?.id) {
          const { type, id } = sel.primitive;
          commit(s => {
            const key = `${type}Hatches`;
            return { ...s, [key]: { ...(s[key] || {}), [id]: { patternId, scale: 1, angle: 0 } } };
          });
        } else if (sel.face) {
          commit(s => ({ ...s, faceHatches: { ...(s.faceHatches || {}), [sel.face]: { patternId, scale: 1, angle: 0 } } }));
        }
        break;
      }
      case "LAYER_STATE_SAVE":
        if (cmd.name) commit(s => mutSaveLayerState(cmd.name, s));
        break;
      case "LAYER_STATE_RESTORE":
        if (cmd.name) commit(s => mutRestoreLayerState(cmd.name, s));
        break;
      case "LAYER_STATE_DELETE":
        if (cmd.name) commit(s => mutDeleteLayerState(cmd.name, s));
        break;
      case "SELECT_ALL": {
        // Select all edges, nodes, symbols, annotations
        const allEdges  = Object.keys(st.edges      || {});
        const allNodes  = Object.keys(st.nodes      || {});
        const allSyms   = Object.keys(st.symbols    || {});
        const allAnns   = Object.values(st.annotations || {}).map(a => "ann_" + a.id);
        setSelMany({ edges:allEdges, nodes:allNodes, symbols:allSyms, annotations:allAnns });
        clearSel();
        break;
      }
      case "OFFSET":
        // OFFSET <dist> — set offset distance and enter offset mode
        if (cmd.dist && cmd.dist > 0) setOffsetDist(cmd.dist);
        setMode("offset");
        setWStart(null);
        break;
      case "OFFSET_EDGE":
        // Direct offset of a specific edge
        commit(s => mutOffsetEdge(cmd.eId, cmd.dist, cmd.side, s));
        break;
      case "OFFSET_FACE":
        commit(s => mutOffsetFace(cmd.faceId, cmd.dist, s));
        break;
      default: break;
    }
  }, [commit, clearSel, undo, redo, matId, wallH, activeLayer, setMode, setWStart, setSnapLock, setZoom,
      selMany, transformAngle, transformScale, arrayRows, arrayCols, arrayRowSpacing, arrayColSpacing,
      arrayCount, arrayTotalAngle, setOffsetDist, setMirrorAxisStart]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Reset origin — shift all nodes so minX=0, minY=0 ─────────────────────────
  const handleResetOrigin = useCallback(() => {
    commit(s => {
      const ns = Object.values(s.nodes || {});
      if (!ns.length) return s;
      const minX = Math.min(...ns.map(n => n.x));
      const minY = Math.min(...ns.map(n => n.y));
      if (minX === 0 && minY === 0) return s;
      const newNodes = {};
      for (const n of ns) {
        newNodes[n.id] = { ...n, x: n.x - minX, y: n.y - minY };
      }
      return { ...s, nodes: newNodes };
    });
  }, [commit]);

  // ── Keyboard shortcuts ────────────────────────────────────────────────────────
  useEffect(() => {
    const k = e => {
      const inI = ["INPUT", "TEXTAREA"].includes(e.target.tagName);

      if (e.key === "Escape") {
        setWStart(null); setSnapLock(null); clearSel(); setEditingDim(null);
        setRoomPopup(null); setOpeningPopup(null); setShowHealLog(false);
        setActiveSymbol(null); setSelAnnotation(null);
        setPendingAnnotation(null); setPendingGridLine(null); setAnnotationDraft("");
        setOffsetHoverEdge(null); setMirrorAxisStart(null); setAdimPts([]); setTrimTarget(null);
        setPrimDrawPts([]);
        setPrimDrawPts([]); setPrimDrawType(null); setSelPrim(null);
        setSelMany({ edges:[], nodes:[], symbols:[], annotations:[] });
        setSelBox(null); setSelBoxAnchor(null);
        setDragSymbol(null); setDragSymPos(null);
        setDragAnn(null); setDragAnnPos(null);
        setDragNodes(null); setDragAnchor(null); setDragDelta({ dx:0, dy:0 });
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "a") {
        e.preventDefault();
        const allEdges  = Object.keys(st.edges      || {});
        const allNodes  = Object.keys(st.nodes      || {});
        const allSyms   = Object.keys(st.symbols    || {});
        const allAnns   = Object.values(st.annotations || {}).map(a => "ann_" + a.id);
        setSelMany({ edges:allEdges, nodes:allNodes, symbols:allSyms, annotations:allAnns });
        clearSel();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) { e.preventDefault(); undo(); return; }
      if ((e.ctrlKey || e.metaKey) && (e.key === "y" || (e.key === "z" && e.shiftKey))) { e.preventDefault(); redo(); return; }
      if (inI) return;

      if (e.key === "w" || e.key === "W") { setMode("wall");    setWStart(null); }
      if (e.key === "s" || e.key === "S")   setMode("select");
      if (e.key === "r" || e.key === "R")   setMode("room");
      if (e.key === "o" || e.key === "O")   setMode("opening");
      if (e.key === "h" || e.key === "H")   commit(mutHeal);
      if (e.key === "t" || e.key === "T")   setShowTemplates(true);
      if (e.key === "a" || e.key === "A")   setMode("annotate");
      if (e.key === "f" || e.key === "F")   setMode("offset");  // oFfset
      if (e.key === "x" || e.key === "X")   setMode("dim");
      if (e.key === "i" || e.key === "I")   setMode("mirror");  // mIrror
      if (e.key === "p" || e.key === "P")   setShowTransformPanel(v => !v); // transform Panel
      if (e.key === "k" || e.key === "K") { setMode("trim"); setTrimTarget(null); } // triK
      if (e.key === "c" || e.key === "C") { setMode("circle"); setPrimDrawPts([]); }
      // Square/Rect: S/R = lines, Shift+S/R = walls
      if ((e.key === "s" || e.key === "S") && !e.shiftKey) { setMode("square"); setPrimDrawPts([]); }
      if ((e.key === "s" || e.key === "S") && e.shiftKey) { setMode("squarewall"); setPrimDrawPts([]); }
      if ((e.key === "r" || e.key === "R") && !e.shiftKey) { setMode("rect"); setPrimDrawPts([]); }
      if ((e.key === "r" || e.key === "R") && e.shiftKey) { setMode("rectwall"); setPrimDrawPts([]); }
      // Enter = finish spline/polyline in progress
      if (e.key === "Enter") {
        if ((mode === "spline" || mode === "polyline") && primDrawPts.length >= 2) {
          if (mode === "spline")   commit(s => mutAddSpline(primDrawPts, false, "walls", s));
          if (mode === "polyline") commit(s => mutAddPolyline(primDrawPts, false, false, "walls", s));
          setPrimDrawPts([]);
        }
      }
      if (e.key === "g" || e.key === "G") {
        const pt = snapRef.current?.point;
        if (pt) { setPendingGridLine({ x: pt.x, y: pt.y }); setAnnotationDraft(""); }
      }
      if (e.key === "d" || e.key === "D")   setShowDrawing(true);
      if (e.key === "l" || e.key === "L")   setShowLayerPanel(v => !v);
      if (e.key === "F11" || (e.key === "f" && e.ctrlKey)) { e.preventDefault(); setIsFullscreen(v => !v); }
      if (e.key === "`")                     setShowCmdLine(v => !v);
      if (e.key === "m" || e.key === "M")    setShowMenuBar(v => !v);
      if (e.key === "c" || e.key === "C") { commit(() => mkEmpty()); clearSel(); }

      if (e.key === "Delete" || e.key === "Backspace") {
        // Multi-select delete
        const hasMany = selMany.edges.length || selMany.symbols.length || selMany.annotations.length;
        if (hasMany) {
          commit(s => mutDeleteMany(selMany, s));
          setSelMany({ edges:[], nodes:[], symbols:[], annotations:[] });
          return;
        }
        if (sel.edge)    { commit(s => mutDeleteEdge(sel.edge, s));       clearSel(); }
        if (sel.opening) { commit(s => mutDeleteOpening(sel.opening, s)); clearSel(); }
        if (selAnnotation) {
          if (selAnnotation.startsWith("ann_")) commit(s => mutDeleteAnnotation(selAnnotation.slice(4), s));
          if (selAnnotation.startsWith("dim_")) commit(s => mutDeleteManualDim(selAnnotation.slice(4), s));
          if (selAnnotation.startsWith("gl_"))  commit(s => mutDeleteGridLine(selAnnotation, s));
          setSelAnnotation(null);
        }
      }
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [undo, redo, sel, selAnnotation, commit, clearSel,
      setMode, setWStart, setSnapLock, setEditingDim, setRoomPopup, setOpeningPopup,
      setShowHealLog, setActiveSymbol, setSelAnnotation,
      setPendingAnnotation, setPendingGridLine, setAnnotationDraft,
      setShowTemplates, setShowDrawing, setShowLayerPanel, snapRef,
      setIsFullscreen, setShowCmdLine, setShowMenuBar,
      setSelMany, setShowTransformPanel, setMirrorAxisStart, setAdimPts, setTrimTarget,
      primDrawPts, setPrimDrawPts, mode, commit]);

  // ── Mouse handlers ─────────────────────────────────────────────────────────────
  const onMM = useCallback(e => {
    if (isPanning.current && panRef.current) {
      setPan({
        x: panRef.current.px + e.clientX - panRef.current.mx,
        y: panRef.current.py + e.clientY - panRef.current.my,
      });
      return;
    }
    const raw = evW(e);
    const sn  = computeSnap(raw, st, grid, snapLock);
    setSnap(sn);
    snapRef.current = sn;
    if (sn.type !== "grid" && !sn.locked) setSnapLock(sn);
    else if (sn.type === "grid")           setSnapLock(null);
    if (dragNode)   setDragPosMm(sn.point);
    if (dragSymbol) setDragSymPos(sn.point);
    // Multi-node drag — track delta from anchor
    if (dragNodes && dragAnchor) {
      setDragDelta({
        dx: sn.point.x - dragAnchor.x,
        dy: sn.point.y - dragAnchor.y,
      });
    }
    if (dragAnn)    setDragAnnPos(sn.point);

    // Rubber-band selection box
    if (selBoxAnchor) {
      setSelBox({ x0: selBoxAnchor.x, y0: selBoxAnchor.y, x1: sn.point.x, y1: sn.point.y });
    }

    // Track hovered edge for offset preview
    if (mode === "offset") {
      setOffsetHoverEdge(sn.edgeId || null);
    }
  }, [evW, st, grid, snapLock, dragNode, mode, isPanning, panRef, setPan, setSnap, setSnapLock, setDragPosMm, snapRef, setOffsetHoverEdge]);

  const onMD = useCallback(e => {
    if (e.button === 1 || (e.button === 0 && e.altKey)) {
      isPanning.current = true;
      panRef.current = { mx: e.clientX, my: e.clientY, px: pan.x, py: pan.y };
      e.preventDefault();
      return;
    }
    // Start rubber-band only when clicking canvas background (not on geometry)
    if (e.button === 0 && mode === "select" && !e.ctrlKey && !e.metaKey) {
      // Don't start rubber-band if clicking on an existing object
      const onObject = e.target.closest(".EH, circle, polygon, line[stroke]");
      const snapOnGeom = snapRef.current?.type === "node" || snapRef.current?.type === "edge";
      if (!onObject && !snapOnGeom) {
        const raw = evW(e);
        setSelBoxAnchor(raw);
        setSelBox(null);
      }
    }
  }, [pan, isPanning, panRef, mode, evW, setSelBoxAnchor, setSelBox]);

  const onMU = useCallback(() => {
    isPanning.current = false;
    panRef.current    = null;

    // Commit single-node drag
    if (dragNode && dragPosMm) commitDrag(s => mutMoveNode(dragNode, dragPosMm, s));
    setDragNode(null);
    setDragPosMm(null);

    // Commit multi-node drag
    if (dragNodes && dragDelta && (Math.abs(dragDelta.dx) > 1 || Math.abs(dragDelta.dy) > 1)) {
      const positions = dragNodes.map(({ id, x0, y0 }) => ({
        id, x: x0 + dragDelta.dx, y: y0 + dragDelta.dy,
      }));
      commitDrag(s => mutMoveNodes(positions, s));
    }
    setDragNodes(null);
    setDragAnchor(null);
    setDragDelta({ dx: 0, dy: 0 });

    // Commit symbol drag
    if (dragSymbol && dragSymPos) {
      commit(s => mutMoveSymbol(dragSymbol, dragSymPos.x, dragSymPos.y, s));
    }
    setDragSymbol(null);
    setDragSymPos(null);

    // Commit annotation drag
    if (dragAnn && dragAnnPos) {
      const annId = dragAnn.startsWith("ann_") ? dragAnn.slice(4) : dragAnn;
      commit(s => mutMoveAnnotation(annId, dragAnnPos.x, dragAnnPos.y, s));
    }
    setDragAnn(null);
    setDragAnnPos(null);

    // Commit rubber-band selection
    if (selBox) {
      const minX = Math.min(selBox.x0, selBox.x1), maxX = Math.max(selBox.x0, selBox.x1);
      const minY = Math.min(selBox.y0, selBox.y1), maxY = Math.max(selBox.y0, selBox.y1);
      const w = maxX - minX, h = maxY - minY;
      if (w > 50 || h > 50) { // only select if box is meaningful
        const edges = Object.values(st.edges).filter(e => {
          const ab = st.halfEdges[e.halfEdgeA], ba = st.halfEdges[e.halfEdgeB];
          if (!ab || !ba) return false;
          const nA = st.nodes[ab.originId], nB = st.nodes[ba.originId];
          if (!nA || !nB) return false;
          const mx = (nA.x + nB.x) / 2, my = (nA.y + nB.y) / 2;
          return mx >= minX && mx <= maxX && my >= minY && my <= maxY;
        }).map(e => e.id);
        const nodes = Object.values(st.nodes).filter(n =>
          n.x >= minX && n.x <= maxX && n.y >= minY && n.y <= maxY
        ).map(n => n.id);
        const symbols = Object.values(st.symbols || {}).filter(s =>
          s.x >= minX && s.x <= maxX && s.y >= minY && s.y <= maxY
        ).map(s => s.id);
        const annotations = Object.values(st.annotations || {}).filter(a =>
          a.x >= minX && a.x <= maxX && a.y >= minY && a.y <= maxY
        ).map(a => "ann_" + a.id);
        setSelMany({ edges, nodes, symbols, annotations });
        clearSel();
      }
      setSelBox(null);
      setSelBoxAnchor(null);
    }
  }, [dragNode, dragPosMm, dragSymbol, dragSymPos,
      commitDrag, commit, isPanning, panRef,
      setDragNode, setDragPosMm, setDragSymbol, setDragSymPos,
      dragNodes, dragDelta, setDragNodes, setDragAnchor, setDragDelta,
      dragAnn, dragAnnPos, setDragAnn, setDragAnnPos,
      selBox, setSelBox, setSelBoxAnchor, setSelMany, clearSel, st]);

  const onWh = useCallback(e => {
    e.preventDefault();
    setZoom(z => Math.min(6, Math.max(0.1, z * (e.deltaY < 0 ? 1.12 : 0.88))));
  }, [setZoom]);

  const onSvgClick = useCallback(e => {
    if (e.target.closest(".EH")) return;
    const now = Date.now();
    if (now - lastClick.current < 280) return;
    lastClick.current = now;

    // Always use freshest snap — avoids stale closure from React state lag
    const pt = snapRef.current?.point || snapWP(evW(e));

    // ── Wall chain ────────────────────────────────────────────────────────────
    if (mode === "wall") {
      if (!wStart) {
        setWStart(pt);
        setSnapLock(null);
      } else {
        if (Geo.dsq(wStart, pt) > WORLD_SNAP * WORLD_SNAP * 0.1) {
          const a = wStart, b = pt, m = matId, h = wallH;
          commit(s => mutAddWall(a, b, "wall", MATERIALS[m].thickness, h, m, s));
          setWStart(pt);
          setSnapLock(null);
          return; // keep chain going
        }
        setWStart(null);
        setSnapLock(null);
      }
      return;
    }

    // ── Annotation ────────────────────────────────────────────────────────────
    if (mode === "annotate") {
      setPendingAnnotation({ x: pt.x, y: pt.y });
      setAnnotationDraft("");
      return;
    }

    // ── Manual dimension — two-click ──────────────────────────────────────────
    if (mode === "dim") {
      if (!wStart) {
        setWStart(pt);
      } else {
        commit(s => mutAddManualDim(wStart, pt, 20, "", "h", s));
        setWStart(null);
      }
      return;
    }

    // ── Trim mode — two-click: edge to trim → cutting edge ───────────────────
    if (mode === "trim") {
      const hoveredEdge = snapRef.current?.edgeId || null;
      if (!trimTarget) {
        if (hoveredEdge) setTrimTarget(hoveredEdge);
      } else {
        if (hoveredEdge && hoveredEdge !== trimTarget) {
          const keepSide = "A";
          commit(s => mutTrim(trimTarget, hoveredEdge, keepSide, s));
          setTrimTarget(null);
        } else if (!hoveredEdge) {
          setTrimTarget(null);
        }
      }
      return;
    }

    // ── Extend mode — two-click: edge to extend → boundary edge ──────────────
    if (mode === "extend") {
      const hoveredEdge = snapRef.current?.edgeId || null;
      if (!trimTarget) {
        if (hoveredEdge) { setTrimTarget(hoveredEdge); }
      } else {
        if (hoveredEdge && hoveredEdge !== trimTarget) {
          commit(s => mutExtend(trimTarget, hoveredEdge, s));
          setTrimTarget(null);
        } else if (!hoveredEdge) { setTrimTarget(null); }
      }
      return;
    }

    // ── Break mode — click edge to split at snap point ───────────────────────
    if (mode === "break") {
      const hoveredEdge = snapRef.current?.edgeId || null;
      if (hoveredEdge) {
        commit(s => mutBreakEdge(hoveredEdge, pt, s));
      }
      return;
    }

    // ── Circle — 2-click: center then radius point ────────────────────────────
    if (mode === "circle") {
      if (primDrawPts.length === 0) {
        setPrimDrawPts([pt]);
      } else {
        const center = primDrawPts[0];
        const r = Math.sqrt((pt.x - center.x) ** 2 + (pt.y - center.y) ** 2);
        if (r > 0.01) commit(s => mutAddCircle(center.x, center.y, r, "walls", s));
        setPrimDrawPts([]);
      }
      return;
    }

    // ── Arc — 3-click: start, point-on-arc, end ───────────────────────────────
    if (mode === "arc") {
      const newPts = [...primDrawPts, pt];
      if (newPts.length < 3) {
        setPrimDrawPts(newPts);
      } else {
        commit(s => mutAddArc3Pt(newPts[0], newPts[1], newPts[2], "walls", s));
        setPrimDrawPts([]);
      }
      return;
    }

    // ── Spline — click to add points, dbl-click or Enter to finish ────────────
    if (mode === "spline") {
      setPrimDrawPts(prev => [...prev, pt]);
      return;
    }

    // ── Polyline — click to add points, dbl-click or Enter to finish ──────────
    if (mode === "polyline") {
      setPrimDrawPts(prev => [...prev, pt]);
      return;
    }

    // ── Square — 2-click: center, then corner to define side length ──────────
    if (mode === "square") {
      if (primDrawPts.length === 0) {
        setPrimDrawPts([pt]);
      } else {
        const center = primDrawPts[0];
        const dx = pt.x - center.x, dy = pt.y - center.y;
        const diag = Math.sqrt(dx*dx + dy*dy);
        const side = diag / Math.sqrt(2); // diagonal = side * sqrt(2), so side = diag / sqrt(2)
        if (side > 0.01) {
          // Create square as a closed polyline (geometric reference line)
          const hx = side / 2;
          const pts = [
            { x: center.x - hx, y: center.y - hx },
            { x: center.x + hx, y: center.y - hx },
            { x: center.x + hx, y: center.y + hx },
            { x: center.x - hx, y: center.y + hx },
          ];
          commit(s => mutAddPolyline(pts, true, false, "walls", s));
        }
        setPrimDrawPts([]);
      }
      return;
    }

    // ── Rectangle — 2-click: opposite corners ────────────────────────────────
    if (mode === "rect") {
      if (primDrawPts.length === 0) {
        setPrimDrawPts([pt]);
      } else {
        const p0 = primDrawPts[0];
        const p1 = pt;
        if (Math.abs(p0.x - p1.x) > 0.01 && Math.abs(p0.y - p1.y) > 0.01) {
          // Create rectangle as a closed polyline (geometric reference line)
          const x0 = Math.min(p0.x, p1.x), x1 = Math.max(p0.x, p1.x);
          const y0 = Math.min(p0.y, p1.y), y1 = Math.max(p0.y, p1.y);
          const pts = [
            { x: x0, y: y0 },
            { x: x1, y: y0 },
            { x: x1, y: y1 },
            { x: x0, y: y1 },
          ];
          commit(s => mutAddPolyline(pts, true, false, "walls", s));
        }
        setPrimDrawPts([]);
      }
      return;
    }

    // ── Square Wall — 2-click: center, then corner to define side length (creates walls)
    if (mode === "squarewall") {
      if (primDrawPts.length === 0) {
        setPrimDrawPts([pt]);
      } else {
        const center = primDrawPts[0];
        const dx = pt.x - center.x, dy = pt.y - center.y;
        const diag = Math.sqrt(dx*dx + dy*dy);
        const side = diag / Math.sqrt(2);
        if (side > 0.01) {
          // Create square as 4 walls forming a closed loop
          let s = st;
          const hx = side / 2;
          const p0 = { x: center.x - hx, y: center.y - hx };
          const p1 = { x: center.x + hx, y: center.y - hx };
          const p2 = { x: center.x + hx, y: center.y + hx };
          const p3 = { x: center.x - hx, y: center.y + hx };
          s = mutAddWall(p0, p1, "wall", 230, wallH, matId, s);
          s = mutAddWall(p1, p2, "wall", 230, wallH, matId, s);
          s = mutAddWall(p2, p3, "wall", 230, wallH, matId, s);
          s = mutAddWall(p3, p0, "wall", 230, wallH, matId, s);
          commit(s);
        }
        setPrimDrawPts([]);
      }
      return;
    }

    // ── Rectangle Wall — 2-click: opposite corners (creates walls)
    if (mode === "rectwall") {
      if (primDrawPts.length === 0) {
        setPrimDrawPts([pt]);
      } else {
        const p0 = primDrawPts[0];
        const p1 = pt;
        if (Math.abs(p0.x - p1.x) > 0.01 && Math.abs(p0.y - p1.y) > 0.01) {
          // Create rectangle as 4 walls
          let s = st;
          const x0 = Math.min(p0.x, p1.x), x1 = Math.max(p0.x, p1.x);
          const y0 = Math.min(p0.y, p1.y), y1 = Math.max(p0.y, p1.y);
          const pa = { x: x0, y: y0 };
          const pb = { x: x1, y: y0 };
          const pc = { x: x1, y: y1 };
          const pd = { x: x0, y: y1 };
          s = mutAddWall(pa, pb, "wall", 230, wallH, matId, s);
          s = mutAddWall(pb, pc, "wall", 230, wallH, matId, s);
          s = mutAddWall(pc, pd, "wall", 230, wallH, matId, s);
          s = mutAddWall(pd, pa, "wall", 230, wallH, matId, s);
          commit(s);
        }
        setPrimDrawPts([]);
      }
      return;
    }

    // ── Angular dimension — 3-click: vertex → armA → armB ────────────────────
    if (mode === "adim") {
      const newPts = [...adimPts, pt];
      if (newPts.length < 3) {
        setAdimPts(newPts);
      } else {
        const [center, ptA, ptB] = newPts;
        commit(s => mutAddAngularDim(center, ptA, ptB, 800, s));
        setAdimPts([]);
      }
      return;
    }

    // ── Mirror mode — two-click axis definition ───────────────────────────────
    if (mode === "mirror") {
      if (!mirrorAxisStart) {
        setMirrorAxisStart(pt);
      } else {
        if (selMany.edges.length) {
          const axisA = mirrorAxisStart, axisB = pt;
          commit(s => mutMirror(selMany, axisA, axisB, false, s));
        }
        setMirrorAxisStart(null);
      }
      return;
    }

    // ── Offset mode — click edge to offset it ────────────────────────────────
    if (mode === "offset") {
      // Find if click is near an edge (use snap.edgeId if available)
      const snapState = snapRef.current;
      const hoverEId = snapState?.edgeId || null;
      if (hoverEId) {
        // Determine side based on which side of the wall the cursor is on
        const pts = getOffsetPoints(hoverEId, 1, "left", st);
        if (pts) {
          // Dot product to determine which side cursor is on
          const dx = pts.nB.x - pts.nA.x, dy = pts.nB.y - pts.nA.y;
          const L  = Math.sqrt(dx*dx + dy*dy) || 1;
          const nx = -dy/L, ny = dx/L; // left perpendicular
          const toCursor = { x: pt.x - pts.nA.x, y: pt.y - pts.nA.y };
          const side = (toCursor.x * nx + toCursor.y * ny) >= 0 ? "left" : "right";
          commit(s => mutOffsetEdge(hoverEId, offsetDist, side, s));
        }
      }
      return;
    }

    // ── Symbol placement ──────────────────────────────────────────────────────
    if (activeSymbol) {
      const layerId = SYMBOL_LAYER_MAP[activeSymbol] || "annotations";
      commit(s => mutPlaceSymbol(activeSymbol, pt.x, pt.y, layerId, 0, "", s));
      return;
    }

    // ── Room / opening mode — handled via popups (see useEffects below) ───────
    if (mode === "room" || mode === "opening") return;

    // ── Deselect ──────────────────────────────────────────────────────────────
    setSel({ face: null, edge: null, node: null, opening: null, symbol: null });
  }, [mode, wStart, evW, commit, matId, wallH, activeSymbol,
      setWStart, setSnapLock, setPendingAnnotation, setAnnotationDraft, setSel,
      snapRef, lastClick, mirrorAxisStart, setMirrorAxisStart, selMany, offsetDist,
      adimPts, setAdimPts, trimTarget, setTrimTarget,
      primDrawPts, setPrimDrawPts]);

  const onSvgDbl = useCallback(() => {
    if (mode === "wall") { setWStart(null); setSnapLock(null); }
    if (mode === "spline" && primDrawPts.length >= 2) {
      commit(s => mutAddSpline(primDrawPts, false, "walls", s));
      setPrimDrawPts([]);
    }
    if (mode === "polyline" && primDrawPts.length >= 2) {
      commit(s => mutAddPolyline(primDrawPts, false, false, "walls", s));
      setPrimDrawPts([]);
    }
    if (mode === "square" || mode === "rect" || mode === "squarewall" || mode === "rectwall") {
      setPrimDrawPts([]); // Cancel drawing
    }
  }, [mode, setWStart, setSnapLock, primDrawPts, commit, setPrimDrawPts]);

  // ── Room popup trigger ────────────────────────────────────────────────────────
  useEffect(() => {
    if (mode === "room" && sel.face) {
      const face = st.faces[sel.face];
      if (!face || !face.centroid) return;
      const sp = w2s(face.centroid, pan, zoom);
      const r  = svgRef.current?.getBoundingClientRect();
      if (r) setRoomPopup({ faceId: sel.face, screenPt: { x: sp.x + r.left - 100, y: sp.y + r.top - 100 } });
    }
  }, [sel.face, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Opening popup trigger ─────────────────────────────────────────────────────
  useEffect(() => {
    if (mode === "opening" && sel.edge) {
      const e  = st.edges[sel.edge]; if (!e) return;
      const ab = st.halfEdges[e.halfEdgeA], ba = st.halfEdges[e.halfEdgeB];
      if (!ab || !ba) return;
      const nA = st.nodes[ab.originId], nB = st.nodes[ba.originId];
      if (!nA || !nB) return;
      const mid = { x: (nA.x + nB.x) / 2, y: (nA.y + nB.y) / 2 };
      const sp  = w2s(mid, pan, zoom);
      const r   = svgRef.current?.getBoundingClientRect();
      if (r) setOpeningPopup({ edgeId: sel.edge, t: 0.5, screenPt: { x: sp.x + r.left, y: sp.y + r.top - 130 } });
    }
  }, [sel.edge, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    handleCommand,
    handleResetOrigin,
    evW,
    onMM, onMD, onMU, onWh,
    onSvgClick, onSvgDbl,
  };
}
